import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Alert,
  LinearProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Snackbar,
  Link,
  alpha,
} from '@mui/material';
import {
  NetworkCheck,
  Refresh,
  Visibility,
  LinkOff,
  BuildCircle,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import '../../../styles/swal-overrides.css';
import {
  Connection,
  ConnectionService,
  GetAllConnectionsParams,
} from '../../../services/connectionService';
import SysDateRangePicker from '../../../components/SysDateRangePicker';
import { formatarDataHoraSegundos, formatarDuracaoTotal } from '../../../utils/formatToView';

const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { startDate: toYmd(first), endDate: toYmd(last) };
};

const parseYmdToLocalDate = (str: string): Date => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const dateToYmd = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface AcceptedTabProps {
  embedded?: boolean;
}

const AcceptedTab: React.FC<AcceptedTabProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = embedded ? theme.palette.success.main : theme.palette.primary.main;

  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConnections, setTotalConnections] = useState(0);

  const currentMonth = getCurrentMonthRange();

  const [startDate, setStartDate] = useState(currentMonth.startDate);
  const [endDate, setEndDate] = useState(currentMonth.endDate);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: GetAllConnectionsParams = {
        page: page + 1,
        limit: pageSize,
        onlyActive: statusFilter === 'active',
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        username: usernameFilter.trim() || undefined,
        ip: ipFilter.trim() || undefined,
      };

      const response = await ConnectionService.getAll(params);

      if (response.success) {
        setConnections(response.data);
        setTotalPages(response.pagination.pages);
        setTotalConnections(response.pagination.total);
      } else {
        setError('Erro ao carregar conexões');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao carregar conexões: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, [page, pageSize, statusFilter]);

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
    setPage(0);
  };

  const handleViewDetails = (connection: Connection) => {
    setSelectedConnection(connection);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedConnection(null);
  };

  const applyFilters = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        setToastMessage('Data de início não pode ser maior que data fim');
        setToastOpen(true);
        return;
      }
    }

    setPage(1);
    loadConnections();
  };

  const clearFilters = () => {
    const range = getCurrentMonthRange();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setStatusFilter('all');
    setUsernameFilter('');
    setIpFilter('');
    setPage(1);
    setError(null);
    setTimeout(() => {
      loadConnections();
    }, 100);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleDisconnectClick = async () => {
    const { value: username } = await Swal.fire({
      title: 'Derrubar Conexão',
      text: 'Informe o nome do usuário PPPoE para derrubar a conexão',
      input: 'text',
      inputLabel: 'Usuário',
      inputPlaceholder: 'Digite o usuário PPPoE...',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'O usuário é obrigatório';
        }
        return null;
      },
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Derrubar',
      cancelButtonText: 'Cancelar',
      customClass: { container: 'swal-zindex' },
    });

    if (!username) return;

    try {
      const result = await ConnectionService.disconnectUser(username.trim());

      if (result.success) {
        await Swal.fire({
          title: 'Sucesso',
          html: result.message,
          icon: 'success',
          customClass: { container: 'swal-zindex' },
        });
        loadConnections();
      } else {
        await Swal.fire({
          title: 'Erro',
          html: result.message,
          icon: 'error',
          customClass: { container: 'swal-zindex' },
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao derrubar conexão';
      await Swal.fire({
        title: 'Erro',
        text: msg,
        icon: 'error',
        customClass: { container: 'swal-zindex' },
      });
    }
  };

  const handleRepararSessoesPresas = async () => {
    try {
      // 1. Primeiro, buscar preview das sessões presas
      const countResult = await ConnectionService.countSessoesPresas();

      if (!countResult.success) {
        await Swal.fire({
          title: 'Erro',
          text: 'Erro ao verificar sessões presas',
          icon: 'error',
          customClass: { container: 'swal-zindex' },
        });
        return;
      }

      const { totalSessoesPresas } = countResult.data;

      if (totalSessoesPresas === 0) {
        await Swal.fire({
          title: 'Tudo certo!',
          text: 'Nenhuma conexão presa encontrada.',
          icon: 'info',
          customClass: { container: 'swal-zindex' },
        });
        return;
      }

      // 2. Mostrar preview e pedir confirmação
      const confirm = await Swal.fire({
        title: 'Remover Conexões Presas',
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p><strong>Foram encontradas conexões presas:</strong></p>
            <ul style="margin: 10px 0;">
              <li><strong>${totalSessoesPresas}</strong> sessão(ões) sem atualização há mais de 1 hora</li>
            </ul>
            <p style="color: #ff9800; margin-top: 12px;">⚠️ Esta ação irá fechar as sessões que não recebem atualização há mais de 1 hora, definindo o horário de parada como o último momento de atualização.</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f57c00',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Remover presas',
        cancelButtonText: 'Cancelar',
        customClass: { container: 'swal-zindex' },
      });

      if (!confirm.isConfirmed) return;

      // 3. Executar correção
      const fixResult = await ConnectionService.repararSessoesPresas();

      if (fixResult.success) {
        await Swal.fire({
          title: 'Concluído',
          html: `
            <div style="text-align: left; font-size: 14px;">
              <p>✅ ${fixResult.data.closedSessions} conexão(ões) presa(s) removida(s)</p>
            </div>
          `,
          icon: 'success',
          customClass: { container: 'swal-zindex' },
        });
        loadConnections();
      } else {
        await Swal.fire({
          title: 'Erro',
          text: fixResult.message || 'Erro ao remover conexões presas',
          icon: 'error',
          customClass: { container: 'swal-zindex' },
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao remover conexões presas';
      await Swal.fire({
        title: 'Erro',
        text: msg,
        icon: 'error',
        customClass: { container: 'swal-zindex' },
      });
    }
  };

  if (loading && connections.length === 0) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando conexões...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filtros */}
      <Paper elevation={2} sx={{ mb: embedded ? 1 : 1, p: 0.9, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Grid container spacing={embedded ? 1 : 2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Usuário"
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              placeholder="Filtrar por usuário"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="IP"
              value={ipFilter}
              onChange={(e) => setIpFilter(e.target.value)}
              placeholder="Filtrar por IP"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                size="small"
              >
                <MenuItem value="all">Todas as conexões</MenuItem>
                <MenuItem value="active">Apenas ativas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SysDateRangePicker
              value={{
                startDate: startDate ? parseYmdToLocalDate(startDate) : null,
                endDate: endDate ? parseYmdToLocalDate(endDate) : null,
              }}
              onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                setStartDate(dateToYmd(range.startDate));
                setEndDate(dateToYmd(range.endDate));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" gap={embedded ? 0.5 : 1}>
              <Button
                variant="contained"
                onClick={applyFilters}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Aplicar
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Conexões */}
      {embedded ? (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Conexões ({totalConnections})
            </Typography>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Remover conexões presas (sem atualização há mais de 30 minutos)">
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<BuildCircle />}
                  onClick={handleRepararSessoesPresas}
                  disabled={loading}
                >
                  Remover presas
                </Button>
              </Tooltip>
              <Tooltip title="Derrubar conexão">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  //menor que small é 0.75rem e 1rem é o tamanho normal
                  startIcon={<LinkOff />}
                  onClick={handleDisconnectClick}
                  disabled={loading}
                >
                  Derrubar conexão
                </Button>
              </Tooltip>
              <Tooltip title="Atualizar">
                <IconButton onClick={loadConnections} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {connections.length === 0 ? (
            <Box textAlign="center" py={2} sx={{ px: 1.5, pb: 1.5 }}>
              <NetworkCheck sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma conexão encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {startDate || endDate || usernameFilter || ipFilter || statusFilter !== 'all'
                  ? 'Nenhuma conexão encontrada com os filtros aplicados'
                  : 'Não há registros de conexão no período selecionado'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${accentColor} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        py: 0.5,
                        borderBottom: `2px solid ${alpha(accentColor, 0.2)}`,
                      },
                    }}>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Início</TableCell>
                      <TableCell>Fim</TableCell>
                      <TableCell>Duração</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>PPPoE Server</TableCell>
                      <TableCell>Interface</TableCell>
                      <TableCell>Download</TableCell>
                      <TableCell>Upload</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {connections.map((connection) => (
                      <TableRow key={connection.radacctid} hover sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{connection.username || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip
                            label={ConnectionService.getStatusLabel(connection)}
                            color={ConnectionService.getStatusColor(connection)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctstarttime
                            ? formatarDataHoraSegundos(connection.acctstarttime)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctstoptime
                            ? formatarDataHoraSegundos(connection.acctstoptime)
                            : 'Em andamento'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctsessiontime
                            ? formatarDuracaoTotal(connection.acctsessiontime)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          <Link
                            href={`http://${connection.framedipaddress}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {connection.framedipaddress || 'N/A'}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.calledstationid || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.nasportid || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctoutputoctets
                            ? ConnectionService.formatBytes(connection.acctoutputoctets)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctinputoctets
                            ? ConnectionService.formatBytes(connection.acctinputoctets)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Tooltip title="Ver detalhes">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(connection)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[20, 50, 75, 100]}
                component="div"
                count={totalConnections}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePageSizeChange}
                labelRowsPerPage="Linhas por pág:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    minHeight: 32,
                    flexWrap: 'nowrap',
                    justifyContent: 'center',
                    gap: 1,
                    p: 0,
                    '& .MuiTablePagination-spacer': { display: 'none' },
                  },
                  '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                  '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                }}
              />
            </>
          )}
        </Paper>
      ) : (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">
                Conexões ({totalConnections})
              </Typography>
              <Box display="flex" gap={0.5}>
                <Tooltip title="Remover conexões presas (sem atualização há mais de 30 minutos)">
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<BuildCircle />}
                    onClick={handleRepararSessoesPresas}
                    disabled={loading}
                  >
                    Remover presas
                  </Button>
                </Tooltip>
                <Tooltip title="Derrubar conexão">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<LinkOff />}
                    onClick={handleDisconnectClick}
                    disabled={loading}
                  >
                    Derrubar conexão
                  </Button>
                </Tooltip>
                <Tooltip title="Atualizar">
                  <IconButton onClick={loadConnections} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {connections.length === 0 ? (
              <Box textAlign="center" py={4}>
                <NetworkCheck sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma conexão encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {startDate || endDate || usernameFilter || ipFilter || statusFilter !== 'all'
                    ? 'Nenhuma conexão encontrada com os filtros aplicados'
                    : 'Não há registros de conexão no período selecionado'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuário</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Início</TableCell>
                        <TableCell>Fim</TableCell>
                        <TableCell>Duração</TableCell>
                        <TableCell>IP</TableCell>
                        {!isMobile && <TableCell>PPPoE Server</TableCell>}
                        {!isMobile && <TableCell>Interface</TableCell>}
                        {!isMobile && <TableCell>Download</TableCell>}
                        {!isMobile && <TableCell>Upload</TableCell>}
                        <TableCell>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {connections.map((connection) => (
                        <TableRow key={connection.radacctid} hover sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}>
                          <TableCell>{connection.username || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={ConnectionService.getStatusLabel(connection)}
                              color={ConnectionService.getStatusColor(connection)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {connection.acctstarttime
                              ? formatarDataHoraSegundos(connection.acctstarttime)
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {connection.acctstoptime
                              ? formatarDataHoraSegundos(connection.acctstoptime)
                              : 'Em andamento'}
                          </TableCell>
                          <TableCell>
                            {connection.acctsessiontime
                              ? formatarDuracaoTotal(connection.acctsessiontime)
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Link
                              href={`http://${connection.framedipaddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {connection.framedipaddress || 'N/A'}
                            </Link>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              {connection.calledstationid || 'N/A'}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {connection.nasportid || 'N/A'}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {connection.acctoutputoctets
                                ? ConnectionService.formatBytes(connection.acctoutputoctets)
                                : 'N/A'}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {connection.acctinputoctets
                                ? ConnectionService.formatBytes(connection.acctinputoctets)
                                : 'N/A'}
                            </TableCell>
                          )}
                          <TableCell>
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(connection)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[20, 50, 75, 100]}
                  component="div"
                  count={totalConnections}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handlePageSizeChange}
                  labelRowsPerPage="Linhas por pág:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  sx={{
                    mt: 2,
                    '.MuiTablePagination-toolbar': {
                      minHeight: 32,
                      flexWrap: 'nowrap',
                      justifyContent: 'center',
                      gap: 1,
                      p: 0,
                      '& .MuiTablePagination-spacer': { display: 'none' },
                    },
                    '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                    '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal de Detalhes */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Detalhes da Conexão</DialogTitle>
        <DialogContent>
          {selectedConnection && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  ID da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctsessionid}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={ConnectionService.getStatusLabel(selectedConnection)}
                  color={ConnectionService.getStatusColor(selectedConnection)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Usuário
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.username || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  IP do Cliente
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <Link
                    href={`http://${selectedConnection.framedipaddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedConnection.framedipaddress || 'N/A'}
                  </Link>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  PPPoE Server
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.calledstationid || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Interface
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.nasportid || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Início da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstarttime
                    ? formatarDataHoraSegundos(selectedConnection.acctstarttime)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Fim da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstoptime
                    ? formatarDataHoraSegundos(selectedConnection.acctstoptime)
                    : 'Em andamento'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Duração Total
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctsessiontime
                    ? formatarDuracaoTotal(selectedConnection.acctsessiontime)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Motivo da Desconexão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctterminatecause
                    ? ConnectionService.getTerminateCauseLabel(
                      selectedConnection.acctterminatecause
                    )
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AcceptedTab;
